/**
 * Results / MVP (design 05 · UI — Results / MVP, CP1zg): the MVP head on hype rays holding the trophy, GAME MVP
 * ribbon and a stat line; FINAL score bar; box score; TOP PLAYS from the match's big moments; ranked Elo change
 * with tier progress; REMATCH / MAIN MENU. Online rematches show the opponent's offer state.
 */
import './results.css';
import { useEffect, useState } from 'preact/hooks';
import { MatchFormat, type ButtonName } from '@hf/shared';
import { useActions, useServices } from '../actions';
import { ActionKey, Art, CoinIcon, Glove, Head, Hints, Key, Rays } from '../components';
import {
  rankProgress,
  rankTier,
  RANK_TIERS,
  rankTierIndex,
  signed,
  thousands,
} from '@hf/client-core/ui-model/format';
import { highlights } from '@hf/client-core/ui-model/moments';
import { useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import type { MatchResultsView } from '@hf/client-core/ui-model/types';
import { isCharacterId } from '@hf/shared';
import { lastPayout, lastPayoutNote } from '../wallet';
import { PORTAL } from '../../portal';
import {
  BLOCK_CONFIRM,
  REPORT_REASONS,
  moderationOutcome,
  runModeration,
} from '@hf/client-core/ui-model/moderation';
import type { PlayerReportReason } from '@hf/client-core/net/types';
import { LESSONS } from '@hf/client-core/ui-model/tutorial/catalog';
import { startTutorial } from '../tutorial/start';
import { promptDevice } from '../hud/common';
import { ReplayTheaterOverlay } from './ReplayTheater';
import {
  RESULTS_ARM_MS,
  REEL_SIZE,
  reelCards,
  reelCaption,
  reelKind,
  soloSide,
  resultLabels,
  coachTip,
  mvpLine,
  BOX_ROWS,
  mvpBadge,
  rematchLabel,
  leaveLabelFor,
  statusLine,
  fmtTime,
} from '@hf/client-core/ui-model/screens';
export {
  RESULTS_ARM_MS,
  REEL_SIZE,
  reelCards,
  reelCaption,
  reelKind,
  soloSide,
  resultLabels,
  type CoachTip,
  coachTip,
  mvpLine,
  BOX_ROWS,
  mvpBadge,
  rematchLabel,
  leaveLabelFor,
  statusLine,
} from '@hf/client-core/ui-model/screens';

/**
 * Effective length of the MVP name for the plate's font fit (`--len`): the plate shrinks its type for names longer
 * than 8 characters so a 16-character name still fits beside the Elo panel (CSS also caps the width + ellipsis).
 */
export const mvpNameLen = (name: string): number => Math.max(8, Math.min(24, [...name.trim()].length));

export function Results() {
  const r = ui.results.value;
  const actions = useActions();
  const { audio, online, replays, input } = useServices();
  const setup = ui.matchSetup.value;
  const isOnline = !!setup?.online;
  const clips = ui.replays.value;
  const playable = !!replays && clips.length > 0;
  const reel = reelCards(clips, playable, highlights.value);
  const [theater, setTheater] = useState<number | null>(null);
  const tapes = clips.slice(0, REEL_SIZE);
  const [rematch, setRematch] = useState<{ you: boolean; opponent: boolean; declined: boolean } | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [modBusy, setModBusy] = useState(false);
  const opponent = setup?.online?.opponent;

  useEffect(() => {
    if (!isOnline) return;
    try {
      return online.on('rematch_state', (m) =>
        setRematch({ you: m.you, opponent: m.opponent, declined: m.declined }),
      );
    } catch {
      return undefined;
    }
  }, [isOnline]);

  const doRematch = (): void => {
    playSfx(audio, UI_SFX.confirm);
    if (isOnline) setRematch((s) => ({ you: true, opponent: s?.opponent ?? false, declined: false }));
    actions.rematch();
  };
  const leave = (): void => {
    playSfx(audio, UI_SFX.back);
    actions.continueFromResults();
  };
  const finishMod = (r: Awaited<ReturnType<typeof runModeration>>, name: string): void => {
    const o = moderationOutcome(r, name);
    ui.toast(o.text, o.tone);
    setModBusy(false);
    setReportOpen(false);
  };
  const doReport = (reason: PlayerReportReason): void => {
    if (!opponent || modBusy) return;
    playSfx(audio, UI_SFX.confirm);
    setModBusy(true);
    void runModeration(online, {
      kind: 'report',
      playerId: opponent.id,
      reason,
      matchId: setup?.online?.matchId,
    }).then((r) => finishMod(r, opponent.name));
  };
  const doBlock = (): void => {
    if (!opponent || modBusy) return;
    if (!window.confirm(BLOCK_CONFIRM(opponent.name))) return;
    playSfx(audio, UI_SFX.confirm);
    setModBusy(true);
    void runModeration(online, { kind: 'block', playerId: opponent.id }).then((r) =>
      finishMod(r, opponent.name),
    );
  };
  const openTape = (i: number): void => {
    if (!playable) return;
    playSfx(audio, UI_SFX.confirm);
    setTheater(i);
  };
  const solo = soloSide(setup);
  const drill = setup?.mode === 'tutorial' || setup?.mode === 'practice';
  const tip = r && solo !== -1 && !drill ? coachTip(r, solo) : null;
  const practise = (): void => {
    if (!tip || solo === -1 || !setup) return;
    playSfx(audio, UI_SFX.confirm);
    if (isOnline) actions.continueFromResults();
    startTutorial(tip.lesson, setup.slots[solo].character, actions.startMatch);
  };
  // Focus order: the playable reel cards, the coach's lesson (after a loss), then REMATCH / MAIN MENU (focused
  // first). Device confirm / back wait RESULTS_ARM_MS: the final whistle often comes while J / A is being mashed.
  const cards = playable ? reel.length : 0;
  const tipIdx = tip ? cards : -1;
  const remIdx = cards + (tip ? 1 : 0);
  const menuIdx = remIdx + 1;
  const reportIdx = opponent ? menuIdx + 1 : -1;
  const blockIdx = opponent ? menuIdx + 2 : -1;
  // An opponent who left (or a lapsed offer) ends the rematch: the button goes dead and focus moves to the exit.
  const declined = isOnline && !!rematch?.declined;
  const leaveLabel = leaveLabelFor(setup);
  const grid = useFocusGrid({
    count: menuIdx + 1 + (opponent ? 2 : 0),
    initial: remIdx,
    isDisabled: (i) => (i === remIdx && declined) || (modBusy && (i === reportIdx || i === blockIdx)),
    active: theater === null,
    armMs: RESULTS_ARM_MS,
    onMove: () => playSfx(audio, UI_SFX.move),
    onConfirm: (i) => {
      if (i < cards) return openTape(i);
      if (i === tipIdx) return practise();
      if (i === remIdx) return declined ? undefined : doRematch();
      if (i === menuIdx) return leave();
      if (i === reportIdx) {
        playSfx(audio, UI_SFX.confirm);
        setReportOpen((v) => !v);
        return;
      }
      if (i === blockIdx) return doBlock();
    },
    onAdjust: (i, dir) => {
      if (i < cards) grid.setIndex(Math.max(0, Math.min(cards - 1, i + dir)));
      else if (i >= remIdx) {
        const last = menuIdx + (opponent ? 2 : 0);
        grid.setIndex(Math.max(remIdx, Math.min(last, i + dir)));
      }
    },
    onBack: leave,
  });

  useEffect(() => {
    if (declined && grid.index === remIdx) grid.setIndex(menuIdx);
  }, [declined]);

  if (!r) return <EmptyResults onLeave={leave} />;
  const mvp = r.lines[r.mvp];
  const mvpChar = isCharacterId(mvp.character) ? mvp.character : 'rookie';
  const labels = resultLabels(r, setup);
  const tipDevice = solo === -1 ? null : promptDevice(setup, solo, input.lastActiveDevice());
  const star = mvpBadge(r);
  // no confetti for a local player who just lost (the CPU's / opponent's trophy still gets its rays)
  const youLost = solo !== -1 && r.winner !== -1 && r.winner !== solo;

  return (
    <div class="hf-screen hf-results" data-screen="results">
      <div class="hf-results__bg" aria-hidden="true">
        <Rays a={r.mvp === 0 ? '#e8343c' : '#2f7df6'} b="#1b1440" rays={18} x="18%" y="48%" speed={60} />
        <div class="hf-results__confetti" hidden={youLost}>
          {Array.from({ length: 24 }, (_, i) => (
            <i key={i} style={{ '--i': i } as Record<string, number>} />
          ))}
        </div>
      </div>

      <section class={`hf-mvp ${star.trophy ? '' : 'gritty'}`} aria-label={star.label}>
        <div class="hf-mvp__ribbon t-display">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            {star.trophy ? (
              <path d="M3 18h18l-1.5-10-4.5 4-3-7-3 7-4.5-4z" fill="currentColor" />
            ) : (
              <path
                d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z"
                fill="currentColor"
              />
            )}
          </svg>
          {star.label}
        </div>
        <div class="hf-mvp__stage">
          <div class="hf-mvp__aura" aria-hidden="true" />
          {/* The MVP always faces the trophy hand (right), whichever side won: the away glove art faces left, so
              it is mirrored to present the trophy with its cuff under the chin. The best player on the LOSING side
              is no trophy-waver: STAR OF THE GAME, a determined face and a clenched fist (QA r3: "did I win?"). */}
          <Head
            character={mvpChar}
            expression={star.trophy ? 'celebrating' : 'determined'}
            class="hf-mvp__head"
          />
          <div class="hf-mvp__hand">
            {star.trophy ? (
              <Art keys={['prop/trophy', 'ui/trophy']} class="hf-mvp__trophy" fallback={<TrophyGlyph />} />
            ) : null}
            <Glove
              side={r.mvp === 0 ? 'home' : 'away'}
              pose={star.trophy ? 'ready' : 'fist'}
              flip={r.mvp === 1}
              class="hf-mvp__glove"
            />
          </div>
        </div>
        <div class={`hf-mvp__plate ${r.mvp === 1 ? 'away' : 'home'}`}>
          <div
            class="hf-mvp__name t-display"
            style={{ '--len': mvpNameLen(mvp.name) } as Record<string, number>}
            title={mvp.name}
          >
            {mvp.name.toUpperCase()}
          </div>
          <div class="hf-mvp__sub t-broadcast">{mvpLine(mvp)}</div>
        </div>
      </section>

      <section class="hf-results__main">
        <FinalBar r={r} labels={labels} you={solo} />
        {PORTAL ? null : <CoinsEarned />}
        <div class="hf-box hf-panel" role="table" aria-label="Box score">
          <div class="hf-box__head" role="row">
            <span class="t-broadcast" role="columnheader">
              BOX SCORE
            </span>
            <span class="t-broadcast home" role="columnheader">
              {labels[0]}
            </span>
            <span class="t-broadcast away" role="columnheader">
              {labels[1]}
            </span>
          </div>
          {BOX_ROWS.map(([label, get]) => {
            const a = get(r.lines[0]);
            const b = get(r.lines[1]);
            const na = parseInt(a, 10);
            const nb = parseInt(b, 10);
            return (
              <div class="hf-box__row" role="row" key={label}>
                <span role="cell">{label}</span>
                <span role="cell" class={`t-broadcast ${na > nb ? 'lead' : ''}`}>
                  {a}
                </span>
                <span role="cell" class={`t-broadcast ${nb > na ? 'lead' : ''}`}>
                  {b}
                </span>
              </div>
            );
          })}
        </div>

        {reel.length > 0 ? (
          <div class="hf-reel">
            <div class="hf-reel__label t-broadcast">TOP PLAYS · INSTANT REPLAY</div>
            <div class="hf-reel__row">
              {reel.map((h) => {
                const body = (
                  <>
                    <div class="hf-reel__screen">
                      {h.clip >= 0 ? <span class="hf-reel__play t-broadcast">▶ PLAY</span> : null}
                      <span class="hf-reel__tc t-broadcast">SP {fmtTime(h.atSec)}</span>
                      <span class="hf-reel__glyph" aria-hidden="true" />
                      {h.character && isCharacterId(h.character) ? (
                        <Head
                          character={h.character}
                          expression={h.expression ?? 'celebrating'}
                          flip={h.side === 1}
                          class="hf-reel__head"
                        />
                      ) : null}
                      <span
                        class="hf-reel__title t-display"
                        style={{ '--len': Math.max(9, h.text.length) } as Record<string, number>}
                      >
                        {h.text}
                      </span>
                    </div>
                    <div class="hf-reel__caption">{reelCaption(h.text, h.sub)}</div>
                  </>
                );
                return h.clip >= 0 ? (
                  <button
                    class={`hf-reel__card playable tone-${h.tone} ${reelKind(h.text)} ${h.side === 1 ? 'away' : ''} interactive`}
                    key={h.key}
                    aria-label={`Watch the replay: ${h.text}`}
                    {...grid.item(h.clip)}
                  >
                    {body}
                  </button>
                ) : (
                  <div
                    class={`hf-reel__card tone-${h.tone} ${reelKind(h.text)} ${h.side === 1 ? 'away' : ''}`}
                    key={h.key}
                  >
                    {body}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div class="hf-results__foot">
          <div class="hf-results__left">
            {/* VICTORY! / DEFEAT headlines every result, ranked included (the Elo change sits over the buttons). */}
            <StatusNote r={r} you={solo} />
            {tip ? (
              <div class="hf-tip hf-panel" role="note" aria-label="Coach's tip">
                <div class="hf-tip__head">
                  <span class="hf-tip__chip t-broadcast">COACH'S TIP</span>
                  <b class="t-display">{tip.title}</b>
                </div>
                <p class="hf-tip__body">
                  <TipText text={tip.body} device={tipDevice} />
                </p>
                <button class="hf-btn blue hf-tip__btn interactive" {...grid.item(tipIdx)}>
                  PRACTICE IT · FOOTBALL 101 · {LESSONS.find((l) => l.id === tip.lesson)?.number ?? ''}{' '}
                  {LESSONS.find((l) => l.id === tip.lesson)?.title ?? ''}
                </button>
              </div>
            ) : null}
          </div>
          <div class="hf-results__right">
            {r.ratingChange ? <EloPanel change={r.ratingChange} /> : null}
            <div class={`hf-results__btns ${grid.armed ? '' : 'arming'}`}>
              <button
                class={`hf-btn hf-results__btn interactive ${declined ? 'declined' : ''}`}
                {...grid.item(remIdx)}
                disabled={declined}
                aria-disabled={declined}
              >
                {declined ? null : <ActionKey action="confirm" />}
                {rematchLabel(isOnline, rematch)}
              </button>
              <button class="hf-btn purple hf-results__btn interactive" {...grid.item(menuIdx)}>
                <ActionKey action="back" />
                {leaveLabel}
              </button>
              {opponent ? (
                <>
                  <button
                    class="hf-btn dark hf-results__btn hf-results__mod interactive"
                    {...grid.item(reportIdx)}
                    disabled={modBusy}
                    onClick={() => {
                      playSfx(audio, UI_SFX.confirm);
                      setReportOpen((v) => !v);
                    }}
                  >
                    REPORT
                  </button>
                  <button
                    class="hf-btn dark hf-results__btn hf-results__mod interactive"
                    {...grid.item(blockIdx)}
                    disabled={modBusy}
                    onClick={doBlock}
                  >
                    BLOCK
                  </button>
                </>
              ) : null}
            </div>
            {reportOpen && opponent ? (
              <div class="hf-results__report" role="dialog" aria-label="Report player">
                <b class="t-broadcast">REPORT {opponent.name.toUpperCase()}</b>
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r.id}
                    class="hf-btn dark interactive"
                    disabled={modBusy}
                    onClick={() => doReport(r.id)}
                  >
                    {r.label}
                  </button>
                ))}
                <button class="hf-btn purple interactive" onClick={() => setReportOpen(false)}>
                  CANCEL
                </button>
              </div>
            ) : null}
          </div>
        </div>
        {/* One line under the buttons; the button label carries the rest (a declined rematch is said by the dead
            button alone: the line and a toast repeated it three times, QA r4) */}
        {isOnline && rematch && !rematch.declined && rematch.opponent !== rematch.you ? (
          <div class="hf-results__rematch t-broadcast" role="status">
            {rematch.opponent ? 'YOUR OPPONENT WANTS A REMATCH!' : 'REMATCH OFFERED · WAITING FOR OPPONENT'}
          </div>
        ) : null}
      </section>
      {theater === null ? (
        <Hints
          class="hf-results__hints"
          items={[
            [
              'confirm',
              grid.index < cards
                ? 'WATCH REPLAY'
                : grid.index === tipIdx
                  ? 'PRACTICE IT'
                  : grid.index === remIdx
                    ? 'REMATCH'
                    : grid.index === reportIdx
                      ? 'REPORT'
                      : grid.index === blockIdx
                        ? 'BLOCK'
                        : leaveLabel,
            ],
            ...(cards > 0 ? ([['up|down', 'TOP PLAYS']] as [string, string][]) : []),
            ['back', leaveLabel],
          ]}
        />
      ) : null}
      {theater !== null ? (
        <ReplayTheaterOverlay
          clips={tapes}
          start={theater}
          onClose={() => {
            // Back on the results, ENTER means REMATCH again (the hint and the keycap say so): focus goes home.
            setTheater(null);
            grid.setIndex(remIdx);
          }}
        />
      ) : null}
    </div>
  );
}

function FinalBar({ r, labels, you }: { r: MatchResultsView; labels: [string, string]; you: 0 | 1 | -1 }) {
  const setup = ui.matchSetup.value;
  const classic = setup?.config.format === MatchFormat.CLASSIC;
  const drives = setup?.config.drivesPerPlayer;
  return (
    <div class="hf-final" aria-label="Final score">
      {([0, 1] as const).map((i) => [
        <div
          class={`hf-final__side ${i === 0 ? 'home' : 'away'} ${r.winner === i ? 'win' : r.winner === -1 ? '' : 'lose'}`}
          key={i}
        >
          <span class="hf-final__abbr t-broadcast">
            {labels[i]}
            {you === i && labels[i] !== 'YOU' ? <i class="hf-final__you">YOU</i> : null}
          </span>
          <span class="hf-final__score t-broadcast">{r.lines[i].score}</span>
        </div>,
        i === 0 ? (
          <div class="hf-final__tag" key="tag">
            <b class="t-display">{r.winner === -1 ? 'NO CONTEST' : 'FINAL'}</b>
            {classic ? (
              <small class="t-broadcast">2 HALVES</small>
            ) : drives ? (
              <small class="t-broadcast">
                {drives} DRIVE{drives === 1 ? '' : 'S'}
              </small>
            ) : null}
          </div>
        ) : null,
      ])}
    </div>
  );
}

function EloPanel({ change }: { change: NonNullable<MatchResultsView['ratingChange']> }) {
  const tier = rankTierIndex(change.after);
  const next = RANK_TIERS[tier + 1];
  const up = change.delta >= 0;
  return (
    <div class={`hf-elo ${up ? 'up' : 'down'}`} aria-label="Rating change">
      <div class="hf-elo__row">
        <span class="hf-elo__l t-broadcast">ELO</span>
        <span class="hf-elo__v t-broadcast">
          {thousands(change.before)} → {thousands(change.after)}
        </span>
        <span class="hf-elo__delta t-broadcast">{signed(change.delta)}</span>
      </div>
      <div class="hf-elo__bar">
        <span class="t-broadcast">{rankTier(change.after)}</span>
        <span class="hf-elo__track">
          <span style={{ width: `${Math.round(rankProgress(change.after) * 100)}%` }} />
        </span>
        <span class="t-broadcast next">{next ? next.name : 'MAX'}</span>
      </div>
    </div>
  );
}

function StatusNote({ r, you }: { r: MatchResultsView; you: 0 | 1 | -1 }) {
  const { big, text } = statusLine(r, you);
  return (
    <div class={`hf-results__status t-broadcast ${big ? (r.winner === you ? 'won' : 'lost') : ''}`}>
      {big ? <b class="t-display">{big}</b> : null}
      <span>{text}</span>
    </div>
  );
}

/** Tip text with "{D}"-style tokens rendered as the player's own keycaps. */
function TipText({ text, device }: { text: string; device: string | null }) {
  const { input } = useServices();
  const parts = text.split(/(\{[A-Z]\})/);
  return (
    <>
      {parts.map((p, i) => {
        const m = /^\{([A-Z])\}$/.exec(p);
        if (!m) return p;
        let label = m[1]!;
        try {
          if (device) label = input.promptFor(device, m[1] as ButtonName);
        } catch {
          /* unknown device: the logical name */
        }
        return <Key key={i} label={label} pad={!!device?.startsWith('pad:')} />;
      })}
    </>
  );
}

function EmptyResults({ onLeave }: { onLeave: () => void }) {
  return (
    <div class="hf-screen hf-results empty" data-screen="results">
      <div class="hf-results__empty hf-panel">
        <div class="t-display">NO RESULTS YET</div>
        <button class="hf-btn interactive" onClick={onLeave}>
          MAIN MENU
        </button>
      </div>
    </div>
  );
}

function TrophyGlyph() {
  return (
    <svg class="hf-mvp__trophy" viewBox="0 0 64 80" aria-label="Trophy">
      <path
        d="M14 6h36v10h10c0 14-8 22-16 23-2 6-6 9-10 10v9h10v8H20v-8h10v-9c-4-1-8-4-10-10C12 38 4 30 4 16h10z"
        fill="#ffd23f"
        stroke="#140c1c"
        stroke-width="4"
        stroke-linejoin="round"
      />
      <path d="M22 12h6v20h-6z" fill="#fff6c8" opacity="0.7" />
    </svg>
  );
}

/** "+N COINS" after a paying match (vs CPU / online quick + ranked); why it paid 0 for a short or capped CPU game. */
function CoinsEarned() {
  const n = lastPayout.value;
  const note = lastPayoutNote.value;
  if (n > 0)
    return (
      <div class="hf-results__coins t-display" data-testid="results-coins" role="status">
        <CoinIcon class="hf-results__coins-icon" />+{n} COINS EARNED
      </div>
    );
  if (!note) return null;
  return (
    <div class="hf-results__coins hf-results__coins--none" data-testid="results-coins-none" role="status">
      {note === 'short'
        ? 'No coins: matches under 45 s don’t pay.'
        : 'No coins: today’s paid CPU matches are done.'}
    </div>
  );
}
